declare var $: any;
//System
import { Component, ElementRef, OnInit, OnDestroy, ViewChild, AfterViewInit } from '@angular/core';
import { catchError, Subject, takeUntil, tap, Subscription } from 'rxjs';
import {Title} from "@angular/platform-browser";
import { SlickCarouselComponent } from 'ngx-slick-carousel';

//Third Party
import * as moment from 'moment';

//Services
import { LogService } from 'src/app/services/common/log.service';
import { ClassesService } from 'src/app/services/classes.service';
import { MetaService } from 'src/app/services/meta.service';
import { EnvironmentService } from 'src/app/services/environment.service';
import { FacilityService } from 'src/app/services/facility.service';
import { AuthService } from 'src/app/services/auth.service';

//Models
import { InstructorDetails, ClassesTab } from 'src/app/models/meta';
import { Studio, Session} from 'src/app/models/studio/session-details';
import {
  PartnerLocationOption,
  ScheduleViewSelection,
  canSeeSharedSchedule,
  excludePartnerArtifacts,
  formatScheduleSelection,
  isPartnerArtifact,
  parseScheduleSelection,
  partnerLocationsForViewer,
  selectionIncludesHost,
  sessionVenueName,
  sessionsForScheduleView,
  toggleHostInSelection,
  togglePartnerInSelection,
} from 'src/app/models/studio/shared-schedule';

//Utils
import { Utils } from 'src/app/services/common/utils';

@Component({
  selector: 'df-todays-schedule',
  templateUrl: './todays-schedule.component.html',
  styleUrls: ['./todays-schedule.component.scss']
})

export class TodaysScheduleComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('slickCarousel', { static: false }) slickCarousel!: SlickCarouselComponent;
  @ViewChild('sessionsWrapper', { static: false }) sessionsWrapperDiv!: ElementRef;

  date: string = moment().format("YYYY-MM-DD");
  
  studioIds: number[] = [];
  instructorIds: number[] = [];

  liveSessionIds: number[] = [];

  sessions : Session[] = [];
  private allSessions: Session[] = [];
  partnerLocations: PartnerLocationOption[] = [];
  scheduleSelection: ScheduleViewSelection = { includeHost: true, partnerKeys: [] };
  sharedScheduleAllowed = false;
  firstVisibleSession! : Session;
  private locations: Array<{ Id: number; TimeZone?: string }> = [];

  currentDate = new Date();
  currentDateYYYYMMDD = "";
  currentDateStr = "";
  
  classesTab!: ClassesTab;
  studios!: Studio[] | undefined;
  instructors!: InstructorDetails[] | undefined;

  classPointerUpdateInterval: number = 1 * 10 * 1000;
  updateMetaInterval: number = 15 * 60 * 1000;

  //Sessions
  private readonly _unsubscribe: Subject<any> = new Subject();

  filteredSessions!: Session[] | null;
  template: string = 'scheduleView';  //Passing itemIds to popup

  private studioSubscription!: Subscription;

  constructor(private titleService:Title
    , private logService: LogService
    , private _classesService: ClassesService
    , private metaService: MetaService
    , private _environmentService: EnvironmentService
    , private facilityService: FacilityService
    , private authService: AuthService
    ) {
    this.titleService.setTitle("Today's Class Schedules - " +this._environmentService.getFacilityConfig().name);
    $("body").attr("id","todays-class-schedule");
  }

  async ngOnInit() {
    this.filteredSessions = null;
    const member = this.authService.getLoggedInMember();
    this.sharedScheduleAllowed = canSeeSharedSchedule(
      this._environmentService.getFacilityConfig().featureFlags,
      member?.Id
    );
    try {
      this.locations = await this.facilityService.getLocations();
    } catch {
      this.locations = [];
    }
    this.updateMeta();

    setInterval(() => {this.updateClassPointer();}, this.classPointerUpdateInterval);
    setInterval(() => {this.updateMeta();}, this.updateMetaInterval);
  }

  /** Session location TZ first, then facility default — never the visitor browser zone. */
  private getSessionTimeZone(session: { LocationId?: number }): string {
    return Utils.resolveSessionTimeZoneFromLocations(
      session?.LocationId,
      this.locations,
      this._environmentService.getFacilityConfig()?.timezone
    );
  }

  get hostScheduleLabel(): string {
    return this._environmentService.getFacilityConfig().name || 'This studio';
  }

  partnerVenue(session: Session): string {
    return isPartnerArtifact(session) ? sessionVenueName(session) : '';
  }

  isHostScheduleSelected(): boolean {
    return selectionIncludesHost(this.scheduleSelection);
  }

  isPartnerLocationSelected(key: string): boolean {
    return this.scheduleSelection.partnerKeys.includes(key);
  }

  selectHostSchedule(): void {
    const next = toggleHostInSelection(this.scheduleSelection);
    if (formatScheduleSelection(next) === formatScheduleSelection(this.scheduleSelection)) {
      return;
    }
    this.scheduleSelection = next;
    this.applyScheduleView();
  }

  selectPartnerLocation(key: string): void {
    this.scheduleSelection = togglePartnerInSelection(this.scheduleSelection, key);
    this.applyScheduleView();
  }

  private applyScheduleView(): void {
    if (!this.sharedScheduleAllowed) {
      this.partnerLocations = [];
      this.scheduleSelection = { includeHost: true, partnerKeys: [] };
      this.sessions = excludePartnerArtifacts(this.allSessions);
    } else {
      this.partnerLocations = partnerLocationsForViewer(this.allSessions, true);
      const validKeys = new Set(this.partnerLocations.map(partner => partner.key));
      this.scheduleSelection = parseScheduleSelection(formatScheduleSelection({
        includeHost: this.scheduleSelection.includeHost,
        partnerKeys: this.scheduleSelection.partnerKeys.filter(key => validKeys.has(key)),
      }));
      this.sessions = sessionsForScheduleView(this.allSessions, this.scheduleSelection)
        .sort((a, b) => this.compareSessions(a, b));
    }
    this.filteredSessions = this.sessions.filter(session => this.isToday(session));
    if (this.sessions.length > 0) {
      this.firstVisibleSession = this.sessions[0];
    }
  }

  private compareSessions(a: Session, b: Session): number {
    const dateA = Utils.getSessionCalendarDateString(a.StartDate);
    const dateB = Utils.getSessionCalendarDateString(b.StartDate);
    if (dateA !== dateB) {
      return dateA < dateB ? -1 : 1;
    }
    if (a.StartTime !== b.StartTime) {
      return a.StartTime < b.StartTime ? -1 : 1;
    }
    return a.Id - b.Id;
  }

  updateClassPointer() {
    this.filteredSessions = this.sessions.filter(session => this.isToday(session));
    this.liveSessionIds = [];
    this.filteredSessions.forEach(session => {
      const sessionTz = this.getSessionTimeZone(session);
      const { start, end } = Utils.combineSessionStartAndEnd(
        session.StartDate,
        session.StartTime,
        session.EndTime || session.StartTime,
        sessionTz
      );
      const now = Date.now();
      if (now >= start.getTime() && now <= end.getTime()) {
        this.liveSessionIds.push(session.Id);
      }
    });
    if(this.liveSessionIds.length > 0)
    {
      var scrollToSelector = `#sessionId-${this.liveSessionIds[0]}`;
      const element = document.querySelector(scrollToSelector) as HTMLElement;
      const element1 = document.querySelector(scrollToSelector);
      if (element && element1) {
        const elementHeight = element.offsetHeight;
        const dataIndex = parseInt(element1.getAttribute('data-index')!);
        $('#sessionsWrapper').animate({
          scrollTop: (dataIndex * elementHeight)
        }, { 
          duration : 1000,
          complete: function() {
            this.disableScrollEvent = false;
          }
        });
      }
    }
  }

  isLiveSession(sessionId: number)
  {
    return false;
    // return this.liveSessionIds.includes(sessionId);
  }

  getTwoDigitString(num: number): string {
    // Convert the number to a string
    let numStr = num.toString();

    // If the number is less than 10, prepend '0' to it
    if (num < 10) {
        numStr = '0' + numStr;
    }

    return numStr;
  }

  updateMeta() {
    this.studioSubscription = this.metaService.getClassesMeta().subscribe((data: ClassesTab) => {
      this.classesTab = data;
      this.studios = excludePartnerArtifacts(this.classesTab.Studios);
      this.instructors = excludePartnerArtifacts(this.classesTab.Instructors);
      this.logService.debug("this.studios",this.studios);
      this.logService.debug("this.instructors",this.instructors);
      
      let todaysDate : string = moment().format("YYYY-MM-DD");

      this.setCurrentDate(todaysDate);
      
      this.fetchSessions().then(() => {
        this.filteredSessions = this.sessions.filter(session => this.isToday(session));
      });
      this.updateClassPointer();
    });
  }

  setCurrentDate(dateTime: string){
    this.currentDate = new Date(dateTime);
    this.date = dateTime;
    let currentMomentDate: moment.Moment = moment(dateTime);
    this.currentDateYYYYMMDD = currentMomentDate.format("YYYY-MM-DD");
    this.currentDateStr = currentMomentDate.format("dddd, MMMM DD");
  }

  getMonth(dateTime: string) {
    let momentDate: moment.Moment = moment(dateTime);
    return momentDate.format("MMM");
  }

  getDate(dateTime: string) {
    let momentDate: moment.Moment = moment(dateTime);
    return momentDate.format("D");
  }

  getDay(dateTime: string) {
    let momentDate: moment.Moment = moment(dateTime);
    return momentDate.format("dd");
  }

  //Sessions
  ngAfterViewInit(): void {
    
  }

  isInFuture(session: Session): boolean {
    const sessionTz = this.getSessionTimeZone(session);
    const { start } = Utils.combineSessionStartAndEnd(
      session.StartDate,
      session.StartTime,
      session.EndTime || session.StartTime,
      sessionTz
    );
    return start.getTime() >= Date.now();
  }

  isToday(session: Session): boolean {
    const sessionTz = this.getSessionTimeZone(session);
    const { end } = Utils.combineSessionStartAndEnd(
      session.StartDate,
      session.StartTime,
      session.EndTime || session.StartTime,
      sessionTz
    );
    const startDateStr = Utils.getSessionCalendarDateString(session.StartDate);
    const todayStr = Utils.getTodayDateStringInTimezone(sessionTz);
    return startDateStr === todayStr && end.getTime() > Date.now();
  }

  getEmptySession(date: Date): Session
  {
    var session: Session = {
      Id: 0,
      LocationId: 0,
      LocationName: '',
      AddressId: null,
      AddressName: '',
      StudioName: '',
      StudioImageUrl: '',
      ClassName: '',
      Theme: '',
      InstructorName: '',
      InstructorImageUrl: '',
      StartDate: date,
      StartTime: '',
      EndTime: '',
      Duration: 0,
      TotalSpots: 0,
      SpotsBooked: 0,
      SpotsLeft: 0
    }
    return session;
  }

  getTimeText(session: { StartDate: Date | string; StartTime: string; LocationId?: number })
  {
    const sessionTz = this.getSessionTimeZone(session);
    const instant = Utils.combineSessionDateAndTime(
      session.StartDate,
      session.StartTime,
      sessionTz
    );
    const label = instant.toLocaleTimeString('en-US', { hour: 'numeric', minute: 'numeric' });
    const abbr = Utils.getShortTimeZoneName(instant);
    return abbr ? `${label} ${abbr}` : label;
  }

  getDateText(date: Date)
  {
    return moment(date).format("dddd, MMMM DD");
  }

  getMilitaryDateText(date: Date)
  {
    return moment(date).format("YYYY-MM-DD");
  }

  getDayText(date: Date)
  {
    return moment(date).format("ddd").toUpperCase();
  }

  /**
   * Handle the error
   * @param {*} error
   */
   _handleError(error: any) {
    // tslint:disable-next-line: prefer-const
    var errorMessage = error.message;
    return errorMessage;
  }

  fetchSessions(): Promise<Session[]> {
    return new Promise((resolve, reject) => {
      this._classesService.getPublicSessions([], [], [], 1, this.sharedScheduleAllowed)
        .pipe(
          takeUntil(this._unsubscribe),
          tap(sessions => {
            this.allSessions = (sessions as Session[]) || [];
            this.applyScheduleView();
            resolve(this.sessions);
          }),
          catchError((error: any) => {
            this.logService.error("ClassSessions error", error);
            reject(this._handleError(error));
            return error;
          })
        )
        .subscribe();
    });
  }

  screenAdoptedName(name: string): string {
    if (window.screen.availWidth < 768) {
      return name.split(' ')[0];
    }
    return name;
  }

  spotsLeftText(session: Session): string {
    return `${session.SpotsLeft} spot${session.SpotsLeft !== 1 ? 's' : ''} left`;
  }

  ngOnDestroy() {
    this._unsubscribe.next(null);
    this._unsubscribe.complete();
    
    if (this.studioSubscription) {
      this.studioSubscription.unsubscribe();
    }
  }
}